import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Stepper,
  Typography,
} from '@mui/material';
import { AccordionCTA } from 'components/ProgressTracker/AccordionCTA';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from '@mui/icons-material';
import { useCurrentAccount, useCurrentCompany } from 'hooks/api';
import { StageSkeleton } from 'components/ProgressTracker/StageSkeleton';
import {
  IAccount,
  ProgressTrackerGroup,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { Business } from 'models/business';
import { ExpandMoreIcon } from 'components/common/Icon';
import { getLatestStatus } from 'helpers/progressTracker';
import { FormationsStep } from 'components/FormationsStep';
import { PersonalDetailsContainer as PersonalDetails } from 'components/ProgressTracker/Incorporation/PersonalDetails';
import { AddressInformationContainer as AddressInformation } from 'components/ProgressTracker/Incorporation/AddressInformation';
import { CompanyDetails1120sOnlyContainer as CompanyDetails } from 'components/ProgressTracker/Incorporation/CompanyDetails1120s';
import { BUSINESS_OWNERSHIP } from 'enums';
import { OwnershipDetailsContainer as OwnershipDetails } from './OwnershipDetails';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

interface Props {
  business: Business;
  account: IAccount;
  isDesktop: boolean;
  isExpanded: boolean;
  expandHandler: () => void;
  onContinueToAccountVerification: () => void;
}

const getStageStatus = (
  group: ProgressTrackerGroup[],
  name: string,
): ProgressTrackerStatus => {
  const stage = group?.find((g) => g.stage === name);
  if (stage) {
    const status = getLatestStatus(stage.status);
    return status?.status ?? ProgressTrackerStatus.NotStarted;
  }
  return ProgressTrackerStatus.NotStarted;
};

const firstStateNotComplete = (company: Business, multiMember: boolean) => {
  if (
    getStageStatus(company.progress, ProgressTrackerStages.PersonalDetails) !==
    ProgressTrackerStatus.Completed
  ) {
    return 0;
  }
  if (
    getStageStatus(company.progress, ProgressTrackerStages.CompanyDetails) !==
    ProgressTrackerStatus.Completed
  ) {
    return 1;
  }
  if (
    getStageStatus(company.progress, ProgressTrackerStages.AddressDetails) !==
    ProgressTrackerStatus.Completed
  ) {
    return 2;
  }
  if (
    multiMember &&
    getStageStatus(company.progress, ProgressTrackerStages.OwnershipDetails) !==
      ProgressTrackerStatus.Completed
  ) {
    return 3;
  }
  return 0;
};

export const Incorporate1120sOnlyUI = ({
  account,
  business,
  isDesktop,
  isExpanded,
  expandHandler,
  onContinueToAccountVerification,
}: Props) => {
  const { t } = useTranslation();
  const isMultiMemberOwnedBusiness =
    business?.businessOwnership === BUSINESS_OWNERSHIP.PARTNERSHIP;

  const [currentStep, setCurrentStep] = React.useState(
    firstStateNotComplete(business, isMultiMemberOwnedBusiness),
  );

  const stageMap = useMemo(
    () => ({
      [ProgressTrackerStages.PersonalDetails]: getStageStatus(
        business?.progress,
        ProgressTrackerStages.PersonalDetails,
      ),
      [ProgressTrackerStages.CompanyDetails]: getStageStatus(
        business?.progress,
        ProgressTrackerStages.CompanyDetails,
      ),
      [ProgressTrackerStages.AddressDetails]: getStageStatus(
        business?.progress,
        ProgressTrackerStages.AddressDetails,
      ),
      [ProgressTrackerStages.OwnershipDetails]: getStageStatus(
        business?.progress,
        ProgressTrackerStages.OwnershipDetails,
      ),
    }),
    [business?.progress],
  );

  const steps = useMemo(() => {
    const baseSteps = [
      {
        key: ProgressTrackerStages.PersonalDetails,
        index: 0,
        label: t('progressTracker.incorporation.step.personalDetails'),
        status: stageMap[ProgressTrackerStages.PersonalDetails],
      },
      {
        key: ProgressTrackerStages.CompanyDetails,
        index: 1,
        label: t('progressTracker.incorporation.step.companyDetails'),
        status: stageMap[ProgressTrackerStages.CompanyDetails],
      },
      {
        key: ProgressTrackerStages.AddressDetails,
        index: 2,
        label: t('progressTracker.incorporation.step.businessAddress'),
        status: stageMap[ProgressTrackerStages.AddressDetails],
      },
    ];
    // In future, if needed to implement the additional owner needed for community states,
    // add the isAdditionalOwnerNeeded boolean here and wherever else you see the isMultiMemberOwnedBusiness boolean in this file
    // it should triggor this ownership details section to be added if the criteria is met once they fill out their business address information
    if (isMultiMemberOwnedBusiness) {
      baseSteps.push({
        key: ProgressTrackerStages.OwnershipDetails,
        index: 3,
        label: t('progressTracker.incorporation.step.ownershipDetails'),
        status: stageMap[ProgressTrackerStages.OwnershipDetails],
      });
    }

    return baseSteps;
  }, [stageMap, isMultiMemberOwnedBusiness]);

  const isStageCompleted = useMemo(() => {
    const baseConditions =
      stageMap[ProgressTrackerStages.PersonalDetails] ===
        ProgressTrackerStatus.Completed &&
      stageMap[ProgressTrackerStages.CompanyDetails] ===
        ProgressTrackerStatus.Completed &&
      stageMap[ProgressTrackerStages.AddressDetails] ===
        ProgressTrackerStatus.Completed;

    const ownershipCondition =
      !isMultiMemberOwnedBusiness ||
      stageMap[ProgressTrackerStages.OwnershipDetails] ===
        ProgressTrackerStatus.Completed;

    return baseConditions && ownershipCondition;
  }, [stageMap, isMultiMemberOwnedBusiness]);

  // jump to the first incomplete step
  useEffect(() => {
    const nextAvailableStep = firstStateNotComplete(
      business,
      isMultiMemberOwnedBusiness,
    );
    if (nextAvailableStep !== currentStep) {
      setCurrentStep(nextAvailableStep);
    }
  }, [business.progress]);

  const completedSteps = useMemo(
    () =>
      steps.filter((step) => step.status === ProgressTrackerStatus.Completed)
        .length,
    [steps],
  );
  const totalSteps = steps.length;

  const onBackClick = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Accordion expanded={isExpanded} onChange={expandHandler}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B">Create Your Account</Typography>
            <Typography variant="subtitle1" color="primary">
              {t('progressTracker.incorporation.subTitle')}
            </Typography>
            <AccordionCTA
              expanded={isExpanded}
              handleExpand={expandHandler}
              completed={completedSteps}
              totalStep={steps.length}
              testId="start-incorporation"
              accordionId="incorporation"
            />
          </Grid>
          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {isStageCompleted && <CheckCircle color="secondary" />}
              {completedSteps}/{totalSteps}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={{ md: 4 }}>
          <Grid item xs={12} md={3}>
            <Stepper
              activeStep={currentStep}
              nonLinear
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              alternativeLabel={!isDesktop}
              data-testid="incorporation-stages"
            >
              {steps.map(({ key, status, label }, index) => (
                <FormationsStep
                  key={key}
                  stageKey={key}
                  index={index}
                  setActiveStep={setCurrentStep}
                  status={status}
                  label={label}
                  isTrainer={false}
                  activeStep={currentStep}
                />
              ))}
            </Stepper>
          </Grid>
          <Grid item xs={12} md={9}>
            {currentStep === 0 && <PersonalDetails business={business} />}
            {currentStep === 1 && (
              <CompanyDetails business={business} />
            )}
            {currentStep === 2 && (
              <AddressInformation
                account={account}
                business={business}
                hasOwnershipSection={isMultiMemberOwnedBusiness}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
            {currentStep === 3 && (
              <OwnershipDetails
                account={account}
                business={business}
                onBackClick={onBackClick}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const Incorporation1120sOnly = (props: {
  isDesktop: boolean;
  isExpanded: boolean;
  expandHandler: () => void;
  onContinueToAccountVerification: () => void;
}) => {
  const { currentCompany: businessData, isLoading: companyDataLoading } =
    useCurrentCompany();
  const { currentAccount, isLoading } = useCurrentAccount();

  if (companyDataLoading && isLoading) {
    return <StageSkeleton />;
  }
  if (businessData && currentAccount) {
    return (
      <Incorporate1120sOnlyUI
        {...props}
        account={currentAccount}
        business={businessData}
      />
    );
  }
  return null;
};
